import { ErrorBoundary } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useMount } from 'react-use';

import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useServerStorage } from 'utils/useServerStorage';

import { DashboardError } from './components/error/dashboardError';
import { DashboardView } from './dashboard.view';
import { ChannelDashboardView } from './views/channel';
import { FavoriteDashboardView } from './views/favorite';
import { QueueDashboardView } from './views/queue';

export const DashboardRoutes = (): JSX.Element => {
  return (
    <ErrorBoundary fallback={<DashboardError />}>
      <Routes>
        <Route path="/" element={<DashboardRedirect />} />

        <Route path="queues/*" element={<DashboardView />}>
          <Route index element={<QueueDashboardView />} />
        </Route>

        <Route path="channels/*" element={<ChannelDashboardView />} />

        <Route path="favorites/*" element={<FavoriteDashboardView />} />

        <Route path="*" element={<Navigate to="/dashboard/queues" replace />} />
      </Routes>
    </ErrorBoundary>
  );
};

/**
 * This component handles redirecting the root '/dashboard' path to the
 * appropriate route depending on persisted state or user preferences.
 */
const DashboardRedirect = (): null => {
  const navigate = useAppNavigate();
  const storage = useServerStorage('user');
  const hasAnalyticsAccess = useHasAccess('analytics');

  const { lastVisitedUrl } = useAppSelector((state) => state.dashboard);
  const { landingPage } = storage.data.dashboard ?? {};

  // On initial mount, redirect to the last visited URL stored in Redux state, to restore the user's
  // previous view when navigating away from and back to the dashboard in the same sessions. Else,
  // if a landing page URL is set in user-storage, redirect there, or default to the queues view.
  useMount(() => {
    navigate(
      lastVisitedUrl ??
        landingPage?.url ?? {
          path: '/dashboard/queues',
          params: {
            search: {
              type: hasAnalyticsAccess ? 'all' : 'agent',
            },
          },
        },
      { replace: true },
    );
  });

  return null;
};
